import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { Box, IconButton, Skeleton, Tab, Tabs, Typography } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ScreenshotSlide, { FlowScreenshotData, ManifestScreenshotData } from './ScreenshotSlide';

export function resolveImageUrl(shot: ManifestScreenshotData, isDark: boolean, viewport: 'mobile' | 'ipad' | 'desktop'): string {
    const prefix = isDark ? 'dark' : 'light';
    const key = `${prefix}_${viewport}` as keyof ManifestScreenshotData;
    const url = shot[key] as string | null;
    if (url) return url;

    const anyLight = shot.light_desktop || shot.light_ipad || shot.light_mobile;
    const anyDark = shot.dark_desktop || shot.dark_ipad || shot.dark_mobile;
    return (isDark ? anyDark : anyLight) || anyLight || anyDark || '';
}

function humanizeStage(name: string): string {
    return name
        .replace(/_/g, ' ')
        .replace(/\b\w/g, c => c.toUpperCase());
}

interface ScreenshotSlideshowProps {
    filterRole?: string;
}

const ScreenshotSlideshow: React.FC<ScreenshotSlideshowProps> = ({ filterRole }) => {
    const [allScreenshots, setAllScreenshots] = useState<FlowScreenshotData[]>([]);

    useEffect(() => {
        fetch('/json/screenshots.json')
            .then(res => res.json())
            .then((data: FlowScreenshotData[]) => setAllScreenshots(data))
            .catch(() => setAllScreenshots([]));
    }, []);

    const roleScreenshots = useMemo(
        () => filterRole
            ? allScreenshots.filter(s => s.role === filterRole)
            : allScreenshots,
        [allScreenshots, filterRole],
    );

    const uniqueStages = useMemo(() => {
        const seen = new Set<string>();
        const ordered: string[] = [];
        for (const s of roleScreenshots) {
            if (!seen.has(s.stage_name)) {
                seen.add(s.stage_name);
                ordered.push(s.stage_name);
            }
        }
        return ordered;
    }, [roleScreenshots]);

    const [activeIndex, setActiveIndex] = useState(0);
    const touchStartX = useRef<number | null>(null);

    const activeStageName = roleScreenshots[activeIndex]?.stage_name ?? uniqueStages[0] ?? null;

    const stageStartIndices = useMemo(() => {
        const map: Record<string, number> = {};
        roleScreenshots.forEach((s, i) => {
            if (!(s.stage_name in map)) map[s.stage_name] = i;
        });
        return map;
    }, [roleScreenshots]);

    useEffect(() => {
        setActiveIndex(0);
    }, [filterRole]);

    const goNext = useCallback(() => {
        setActiveIndex(prev => Math.min(prev + 1, roleScreenshots.length - 1));
    }, [roleScreenshots.length]);

    const goPrev = useCallback(() => {
        setActiveIndex(prev => Math.max(prev - 1, 0));
    }, []);

    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            if (e.key === 'ArrowRight') goNext();
            else if (e.key === 'ArrowLeft') goPrev();
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [goNext, goPrev]);

    const handleTouchStart = (e: React.TouchEvent) => {
        touchStartX.current = e.touches[0].clientX;
    };

    const handleTouchEnd = (e: React.TouchEvent) => {
        if (touchStartX.current === null) return;
        const diff = touchStartX.current - e.changedTouches[0].clientX;
        const SWIPE_THRESHOLD = 50;
        if (diff > SWIPE_THRESHOLD) goNext();
        else if (diff < -SWIPE_THRESHOLD) goPrev();
        touchStartX.current = null;
    };

    const handleStageChange = (_: React.SyntheticEvent, stageName: string) => {
        const idx = stageStartIndices[stageName];
        if (idx !== undefined) setActiveIndex(idx);
    };

    if (allScreenshots.length === 0) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, mt: 3 }}>
                <Skeleton variant="rounded" width="100%" height="50vh" />
                <Skeleton variant="text" width={300} height={32} />
                <Skeleton variant="text" width={500} height={24} />
            </Box>
        );
    }

    if (roleScreenshots.length === 0) {
        return (
            <Box sx={{ textAlign: 'center', py: 8 }}>
                <Typography variant="h6" color="text.secondary">
                    No screenshots available for this selection.
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ width: '100%' }}>
            <Box
                onTouchStart={handleTouchStart}
                onTouchEnd={handleTouchEnd}
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    width: '100%',
                }}
            >
                {roleScreenshots.map((shot, index) => (
                    <ScreenshotSlide
                        key={shot.id}
                        screenshot={shot}
                        visible={index === activeIndex}
                    />
                ))}

                <Box sx={{ position: 'relative', minHeight: 60, width: '100%', textAlign: 'center', mt: 2 }}>
                    {roleScreenshots.map((shot, index) => {
                        const isActive = index === activeIndex;
                        return (
                            <Box
                                key={shot.id}
                                sx={{
                                    ...(index !== 0 && { position: 'absolute', top: 0, left: 0, width: '100%' }),
                                    opacity: isActive ? 1 : 0,
                                    transition: 'opacity 0.4s ease-in-out',
                                    pointerEvents: isActive ? 'auto' : 'none',
                                    px: 2,
                                }}
                            >
                                {shot.title && (
                                    <Typography variant="h6" gutterBottom>
                                        {shot.title}
                                    </Typography>
                                )}
                                {shot.description && (
                                    <Typography variant="body1" color="text.secondary">
                                        {shot.description}
                                    </Typography>
                                )}
                            </Box>
                        );
                    })}
                </Box>
            </Box>

            {uniqueStages.length > 0 && activeStageName !== null && (
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                    <Tabs
                        value={activeStageName}
                        onChange={handleStageChange}
                        variant="scrollable"
                        scrollButtons="auto"
                        aria-label="Select stage"
                    >
                        {uniqueStages.map(name => (
                            <Tab
                                key={name}
                                value={name}
                                label={humanizeStage(name)}
                            />
                        ))}
                    </Tabs>
                </Box>
            )}

            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 2,
                    mt: 2,
                }}
            >
                <IconButton
                    onClick={goPrev}
                    disabled={activeIndex === 0}
                    aria-label="Previous screenshot"
                    size="large"
                >
                    <ArrowBackIosNewIcon />
                </IconButton>

                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80, textAlign: 'center' }}>
                    {activeIndex + 1} of {roleScreenshots.length}
                </Typography>

                <IconButton
                    onClick={goNext}
                    disabled={activeIndex === roleScreenshots.length - 1}
                    aria-label="Next screenshot"
                    size="large"
                >
                    <ArrowForwardIosIcon />
                </IconButton>
            </Box>
        </Box>
    );
};

export default ScreenshotSlideshow;
